import {
  CLASSIC_ROSTER,
  CLASSIC_RULESET,
  haloOf,
  type Ship,
  type Shot,
  shipCount,
} from '@sea-wars/game-core';
import {
  type ComponentType,
  type KeyboardEvent,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type DemoStep, HelpDemo } from './HelpDemo.js';

const CONTACT_EMAIL = 'seawars@pi24.pl';

type TabId = 'rules' | 'controls' | 'invite' | 'app';

const TABS: readonly { readonly id: TabId; readonly label: string }[] = [
  { id: 'rules', label: 'Zasady' },
  { id: 'controls', label: 'Sterowanie' },
  { id: 'invite', label: 'Zapraszanie' },
  { id: 'app', label: 'Aplikacja' },
];

/** Pomoc otwiera się tam, gdzie gracz jej najpewniej potrzebuje: w partii — sterowanie. */
function defaultTab(pathname: string): TabId {
  if (pathname.startsWith('/game/')) return 'controls';
  if (pathname === '/') return 'invite';
  if (pathname === '/konto') return 'app';
  return 'rules';
}

// ── Przykłady: stany planszy 6×6 składane z prawdziwych reguł (`game-core`) ──────────────

const DEMO_SIZE = 6;

const miss = (x: number, y: number): Shot => ({ x, y, result: 'miss' });
const hit = (x: number, y: number): Shot => ({ x, y, result: 'hit' });

/** Zatopienie trójmasztowca: celownik, pudło, dwa trafienia, zatopienie i odkryta aureola. */
const SINK_TARGET: Ship = { id: 1, size: 3, x: 2, y: 1, dir: 'h' };
const SINK_HALO: Shot[] = haloOf(SINK_TARGET, DEMO_SIZE).map((c) => miss(c.x, c.y));
const SHOOTING_STEPS: readonly DemoStep[] = [
  { caption: 'Dotknij pola na planszy przeciwnika — pojawia się celownik.', aim: { x: 1, y: 4 } },
  {
    caption: 'Dotknij tego samego pola jeszcze raz — strzał. Pudło oddaje ruch przeciwnikowi.',
    shots: [miss(1, 4)],
  },
  {
    caption: 'Znów Twoja kolej — celujesz w kolejne pole.',
    shots: [miss(1, 4)],
    aim: { x: 3, y: 1 },
  },
  { caption: 'Trafienie! Po trafieniu strzelasz dalej.', shots: [miss(1, 4), hit(3, 1)] },
  {
    caption: 'Kolejne trafienie — statek jeszcze pływa.',
    shots: [miss(1, 4), hit(3, 1), hit(2, 1)],
  },
  {
    caption: 'Zatopiony! Pola wokół statku odkrywają się same — tam nic nie stoi.',
    shots: [
      miss(1, 4),
      hit(3, 1),
      hit(2, 1),
      { x: 4, y: 1, result: 'sunk', shipId: 1 },
      ...SINK_HALO,
    ],
    fleet: [SINK_TARGET],
  },
];

/** Rozstawianie: kolejne statki, obrót podwójnym dotknięciem, gotowa flota. */
const FOUR: Ship = { id: 0, size: 4, x: 0, y: 0, dir: 'h' };
const THREE_H: Ship = { id: 1, size: 3, x: 0, y: 2, dir: 'h' };
const THREE_V: Ship = { id: 1, size: 3, x: 5, y: 2, dir: 'v' };
const TWO: Ship = { id: 2, size: 2, x: 2, y: 5, dir: 'h' };
const ONE: Ship = { id: 3, size: 1, x: 0, y: 4, dir: 'h' };
const PLACEMENT_STEPS: readonly DemoStep[] = [
  { caption: 'Przeciągnij statek na planszę — albo dotknij statek, a potem pole.', fleet: [FOUR] },
  { caption: 'Kolejny statek…', fleet: [FOUR, THREE_H] },
  { caption: 'Podwójne dotknięcie obraca postawiony statek.', fleet: [FOUR, THREE_V] },
  {
    caption: 'Statki nie mogą się stykać, nawet rogami — przy krawędzi planszy mogą stać.',
    fleet: [FOUR, THREE_V, TWO, ONE],
  },
];

/** Ostatnia seria przeciwnika na własnej planszy — kółko i zakreślacz. */
const OWN_FLEET: Ship[] = [
  { id: 0, size: 3, x: 1, y: 1, dir: 'h' },
  { id: 1, size: 2, x: 4, y: 3, dir: 'v' },
  { id: 2, size: 1, x: 0, y: 4, dir: 'h' },
];
const EARLIER: Shot[] = [miss(5, 0), miss(2, 4)];
const VOLLEY_STEPS: readonly DemoStep[] = [
  { caption: 'Twoja flota i wcześniejsze strzały przeciwnika.', fleet: OWN_FLEET, shots: EARLIER },
  {
    caption: 'Przeciwnik trafia…',
    fleet: OWN_FLEET,
    shots: [...EARLIER, hit(2, 1)],
    recent: [{ x: 2, y: 1 }],
  },
  {
    caption: '…i strzela dalej, aż spudłuje. Cała jego ostatnia seria jest zakreślona.',
    fleet: OWN_FLEET,
    shots: [...EARLIER, hit(2, 1), hit(3, 1), miss(3, 3)],
    recent: [
      { x: 2, y: 1 },
      { x: 3, y: 1 },
      { x: 3, y: 3 },
    ],
  },
];

// ── Treść ─────────────────────────────────────────────────────────────────────────────

const SHIP_NAMES: Record<1 | 2 | 3 | 4, string> = {
  4: 'czteromasztowiec',
  3: 'trójmasztowce',
  2: 'dwumasztowce',
  1: 'jednomasztowce',
};

function RulesTab() {
  const size = CLASSIC_RULESET.boardSize;
  return (
    <>
      <p>
        Dwóch graczy, każdy ze swoją ukrytą flotą na planszy {size}×{size}. Kto pierwszy zatopi
        wszystkie statki przeciwnika, wygrywa.
      </p>
      <ul>
        <li>
          Flota:{' '}
          {([4, 3, 2, 1] as const)
            .map((shipSize) => `${CLASSIC_ROSTER[shipSize]}× ${SHIP_NAMES[shipSize]}`)
            .join(', ')}{' '}
          — razem {shipCount(CLASSIC_ROSTER)} statków.
        </li>
        <li>Statki nie mogą się stykać, nawet rogami; mogą dotykać krawędzi planszy.</li>
        <li>Kto zaczyna — losuje gra.</li>
        <li>
          <strong>Trafienie</strong> daje kolejny strzał, <strong>pudło</strong> oddaje ruch.
        </li>
        <li>Po zatopieniu statku pola wokół niego odkrywają się same.</li>
        <li>
          Nie ma limitu czasu na ruch. Ale jeśli ktoś straci połączenie na dłużej niż 2 minuty,
          przegrywa walkowerem. Partię można też zakończyć przyciskiem „Poddaj się”, a przed
          pierwszym strzałem — „Anuluj partię” (bez porażki, nie liczy się do statystyk).
        </li>
      </ul>
      <HelpDemo steps={SHOOTING_STEPS} label="Przykład: strzelanie i zatopienie statku" />
    </>
  );
}

function ControlsTab() {
  return (
    <>
      <h3 className="hand">Rozstawianie floty</h3>
      <ul>
        <li>Przeciągnij statek na planszę albo dotknij go, a potem pola, na którym ma stanąć.</li>
        <li>
          Podwójne dotknięcie obraca postawiony statek (na komputerze też „Obróć (R)” albo klawisz
          R).
        </li>
        <li>
          „Losuj” ustawia całą flotę za Ciebie, „Jak ostatnio” — tak jak w poprzedniej partii.
        </li>
        <li>„Cofnij” i „Wyczyść” poprawiają ułożenie. Gdy wszystko stoi — „Gotów”.</li>
        <li>Rozmyśliłeś się? „Anuluj partię” kończy ją bez porażki, póki nikt nie strzelił.</li>
      </ul>
      <HelpDemo steps={PLACEMENT_STEPS} label="Przykład: rozstawianie floty" seed={11} />

      <h3 className="hand">Strzelanie</h3>
      <ul>
        <li>
          Przy planszy „Wróg” widać, czyja kolej: <em>Twoja kolej</em> albo <em>Czekaj</em>. Gdy
          czekasz, plansza jest przygaszona.
        </li>
        <li>
          Pierwsze dotknięcie pola ustawia celownik (znaczek pokazuje „Strzał”), drugie — w to samo
          pole — strzela. Dzięki temu przewijanie strony nie odda przypadkowego strzału.
        </li>
        <li>Zatopione statki przeciwnika są zacieniowane.</li>
      </ul>

      <h3 className="hand">Twoja plansza</h3>
      <p>Ostatnia seria strzałów przeciwnika jest obrysowana kółkiem i lekko zakreślona.</p>
      <HelpDemo steps={VOLLEY_STEPS} label="Przykład: ostatnia seria przeciwnika" seed={23} />
    </>
  );
}

function InviteTab() {
  return (
    <>
      <h3 className="hand">Gra z kimś, kto ma konto</h3>
      <ol>
        <li>
          W lobby, na liście graczy, znajdź go (pole „Szukaj po nicku”). Kropka przy nicku mówi, czy
          jest <em>online</em>, <em>w grze</em>, czy <em>offline</em>.
        </li>
        <li>
          Kliknij „Wyzwij”. Przeciwnik dostaje okienko z przyciskami „Graj”, „Odrzuć”, „Później”.
        </li>
        <li>Po „Graj” oboje trafiacie od razu do rozstawiania floty.</li>
      </ol>
      <p>
        Gwiazdka ☆ dodaje gracza do ulubionych — ulubieni są na górze listy. Po partii „Zagraj
        rewanż” proponuje kolejną z tym samym przeciwnikiem.
      </p>

      <h3 className="hand">Znajomy bez konta</h3>
      <ol>
        <li>Otwórz „Moje konto” i znajdź zaproszenie do rejestracji.</li>
        <li>„Wyślij zaproszenie…” (albo WhatsApp, SMS, „Kopiuj”) wysyła mu link.</li>
        <li>Po założeniu konta pojawi się na Twojej liście graczy — wtedy go wyzwij.</li>
      </ol>

      <h3 className="hand">Bez przeciwnika</h3>
      <p>„Zagraj z botem” w lobby — trzy poziomy: Łatwy, Średni, Trudny.</p>
    </>
  );
}

function AppTab() {
  return (
    <>
      <ul>
        <li>
          <strong>Lobby</strong> — napis „Statki” w nagłówku zawsze tu prowadzi. Lista graczy,
          wyzwania, gra z botem i Twoi ostatni przeciwnicy.
        </li>
        <li>
          <strong>Moje konto</strong> — statystyki, historia partii z powtórką ruch po ruchu,
          ranking, wygląd (zeszyt albo tablica, jasny albo ciemny), zmiana hasła, instalacja
          aplikacji na telefonie, wylogowanie.
        </li>
        <li>
          <strong>Kilka urządzeń</strong> — możesz grać tę samą partię na telefonie i komputerze.
          Pasek „Wróć do gry” pokazuje trwającą partię na każdym ekranie; po otwarciu aplikacji
          trafisz do niej od razu.
        </li>
        <li>
          <strong>Połączenie</strong> — po utracie sieci aplikacja łączy się sama i dociąga zaległe
          ruchy. Masz na to 2 minuty, potem partia przepada walkowerem.
        </li>
        <li>
          <strong>Co nowego</strong> — lista zmian pokazuje się przy starcie po każdej aktualizacji.
          Numer wersji jest w stopce strony: jeśli na dwóch urządzeniach jest inny, to starsze nie
          dostało jeszcze aktualizacji (odśwież je albo usuń dane witryny).
        </li>
      </ul>
      <p>
        Gra jest w wersji beta. Błąd albo pomysł? Napisz na{' '}
        <a className="whats-new__mail" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>
        .
      </p>
    </>
  );
}

const TAB_CONTENT: Record<TabId, ComponentType> = {
  rules: RulesTab,
  controls: ControlsTab,
  invite: InviteTab,
  app: AppTab,
};

interface HelpDialogProps {
  readonly open: boolean;
  readonly pathname: string;
  readonly onClose: () => void;
}

/**
 * Pomoc: poruszanie się po aplikacji, zapraszanie, zasady i sterowanie, z animowanymi
 * przykładami. Okno nad bieżącym ekranem, nie osobna strona — otwarte w trakcie partii
 * niczego nie przerywa. Zakładki zamiast jednej długiej ściany tekstu; startowa zależy od
 * ekranu, z którego otwarto pomoc. Animacje żyją tylko w aktywnej zakładce.
 */
export function HelpDialog({ open, pathname, onClose }: HelpDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const tabRefs = useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});
  const [tab, setTab] = useState<TabId>(() => defaultTab(pathname));

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setTab(defaultTab(pathname));
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open, pathname]);

  function handleCancel(event: SyntheticEvent<HTMLDialogElement>) {
    event.preventDefault();
    onClose();
  }

  /** Strzałki między zakładkami — wzorzec „tabs” z ARIA Authoring Practices. */
  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const index = TABS.findIndex((item) => item.id === tab);
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = (index + 1) % TABS.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = TABS.length - 1;
    const target = next === null ? undefined : TABS[next];
    if (!target) return;
    event.preventDefault();
    setTab(target.id);
    tabRefs.current[target.id]?.focus();
  }

  const Content = TAB_CONTENT[tab];

  return (
    <dialog
      ref={dialogRef}
      className="help-dialog"
      aria-labelledby="help-title"
      onCancel={handleCancel}
    >
      <div className="help-dialog__head">
        <h2 id="help-title" className="hand">
          Pomoc
        </h2>
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          Zamknij
        </button>
      </div>

      <div className="help-dialog__tabs" role="tablist" aria-label="Działy pomocy">
        {TABS.map((item) => (
          <button
            key={item.id}
            ref={(node) => {
              tabRefs.current[item.id] = node;
            }}
            type="button"
            role="tab"
            id={`help-tab-${item.id}`}
            aria-selected={tab === item.id}
            aria-controls="help-panel"
            tabIndex={tab === item.id ? 0 : -1}
            className="help-dialog__tab hand"
            onClick={() => setTab(item.id)}
            onKeyDown={onTabKeyDown}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* `key`: zmiana zakładki montuje treść od nowa — przykłady startują od pierwszego kadru. */}
      <div
        key={tab}
        id="help-panel"
        className="help-dialog__panel"
        role="tabpanel"
        aria-labelledby={`help-tab-${tab}`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: przewijany panel musi dać się przewinąć z klawiatury
        tabIndex={0}
      >
        {open && <Content />}
      </div>
    </dialog>
  );
}
